import { inject, Service } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { UserService } from '@core/providers/user/user.service';
import { DialogService } from '@shared/components/dialog/dialog.service';
import { ResultTone } from '@shared/components/result-dialog/result-report';
import { CONSTANTS } from '@shared/constants';

/**
 * Holds a user on their profile until it is filled in. The sign-in redirect alone does not survive
 * a reload or a link straight into a venue, and both land somewhere that shows the blank name.
 */
@Service()
export class ProfileCompletionGuard {
  private userService = inject(UserService);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private isPrompting = false;

  canActivate(): boolean | UrlTree {
    if (!this.userService.isProfileIncomplete$.getValue()) {
      return true;
    }

    this.promptToComplete();

    return this.router.createUrlTree([CONSTANTS.ROUTES.PROFILE]);
  }

  /** One dialog however many links are clicked; the profile points at the name once it closes. */
  private promptToComplete(): void {
    if (this.isPrompting) {
      return;
    }

    this.isPrompting = true;
    this.dialogService
      .resultDialog({
        tone: ResultTone.Info,
        title: { key: 'SHARED.MY_PROFILE.COMPLETE_FIRST_TITLE' },
        message: { key: 'SHARED.MY_PROFILE.COMPLETE_FIRST' },
      })
      .afterClosed()
      .subscribe(() => {
        this.isPrompting = false;
        this.userService.profileCompletionPrompted$.next();
      });
  }
}
